import React, { useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Calendar, Users, MapPin, Phone, Mail, User } from 'lucide-react';
import { useForm } from 'react-hook-form';

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #d4af37 0%, #b8941f 100%);
  color: white;
  padding: 3rem 0;
  text-align: center;
  border-radius: 0 0 20px 20px;
  margin-bottom: 3rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: bold;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
`;

const ReservationForm = styled(motion.form)`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 3rem;
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: #2c3e50;
  font-size: 1.3rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  color: #2c3e50;
  font-weight: 500;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
  }
  
  &.error {
    border-color: #e74c3c;
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
  }
  
  &.error {
    border-color: #e74c3c;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
  }
  
  &.error {
    border-color: #e74c3c;
  }
`;

const ErrorMessage = styled.span`
  color: #e74c3c;
  font-size: 0.9rem;
  margin-top: 0.25rem;
`;

const SubmitButton = styled.button`
  background: #d4af37;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  
  &:hover {
    background: #b8941f;
    transform: translateY(-2px);
  }
  
  &:disabled {
    background: #95a5a6;
    cursor: not-allowed;
    transform: none;
  }
`;

const RestaurantCard = styled.div<{ selected: boolean }>`
  background: white;
  border: 2px solid ${props => props.selected ? '#d4af37' : '#e9ecef'};
  border-radius: 10px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1rem;
  
  &:hover {
    border-color: #d4af37;
    transform: translateY(-2px);
  }
  
  h4 {
    color: #2c3e50;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
  }
  
  p {
    color: #7f8c8d;
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const SuccessMessage = styled(motion.div)`
  background: #27ae60;
  color: white;
  padding: 1.5rem;
  border-radius: 10px;
  text-align: center;
  margin-bottom: 2rem;
  
  h3 {
    margin-bottom: 0.5rem;
  }
  
  p {
    opacity: 0.9;
  }
`;

interface ReservationFormData {
  name: string;
  email: string;
  phone: string;
  restaurant: string;
  date: string;
  time: string;
  guests: string;
  specialRequests: string;
}

const restaurants = [
  {
    id: '1',
    name: 'Poule & Poulette Brussel Centrum',
    address: 'Grote Markt 15, 1000 Brussel',
    phone: '+32 2 123 45 67'
  },
  {
    id: '2',
    name: 'Poule & Poulette Antwerpen',
    address: 'Meir 45, 2000 Antwerpen',
    phone: '+32 3 123 45 67'
  },
  {
    id: '3',
    name: 'Poule & Poulette Gent',
    address: 'Korenmarkt 12, 9000 Gent',
    phone: '+32 9 123 45 67'
  },
  {
    id: '4',
    name: 'Poule & Poulette Leuven',
    address: 'Oude Markt 8, 3000 Leuven',
    phone: '+32 16 123 45 67'
  },
  {
    id: '5',
    name: 'Poule & Poulette Brugge',
    address: 'Markt 20, 8000 Brugge',
    phone: '+32 50 123 45 67'
  }
];

const timeSlots = [
  '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00'
];

const ReservationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [selectedRestaurant, setSelectedRestaurant] = useState(
    location.state?.selectedRestaurant || searchParams.get('restaurant') || ''
  );
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<ReservationFormData>();

  const onSubmit = async (data: ReservationFormData) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    console.log('Reservation data:', { ...data, restaurant: selectedRestaurant });
    setIsSubmitted(true);
    reset();
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  if (isSubmitted) {
    return (
      <PageContainer>
        <SuccessMessage
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h3>🎉 Reservering Bevestigd!</h3>
          <p>
            Bedankt voor je reservering. We hebben een bevestigingsmail gestuurd naar je e-mailadres. 
            We kijken ernaar uit om je te verwelkomen!
          </p>
        </SuccessMessage>
        
        <ReservationForm
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <SubmitButton onClick={() => setIsSubmitted(false)}>
            Nieuwe Reservering
          </SubmitButton>
        </ReservationForm>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>Reserveren</HeroTitle>
        <HeroSubtitle>
          Maak een reservering bij een van onze restaurants
        </HeroSubtitle>
      </HeroSection>

      <ReservationForm
        onSubmit={handleSubmit(onSubmit)}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <FormSection>
          <SectionTitle>
            <MapPin size={20} />
            Kies een Restaurant
          </SectionTitle>
          {selectedRestaurant && (
            <div style={{ 
              background: '#FFD700', 
              color: '#000000', 
              padding: '1rem', 
              borderRadius: '8px', 
              marginBottom: '1rem',
              fontWeight: 'bold'
            }}>
              🎯 Geselecteerd: {selectedRestaurant}
              {location.state?.restaurantAddress && (
                <div style={{ fontSize: '0.9rem', marginTop: '0.5rem', opacity: 0.8 }}>
                  📍 {location.state.restaurantAddress}
                </div>
              )}
            </div>
          )}
          {restaurants.map((restaurant) => (
            <RestaurantCard
              key={restaurant.id}
              selected={selectedRestaurant === restaurant.id}
              onClick={() => setSelectedRestaurant(restaurant.id)}
            >
              <h4>{restaurant.name}</h4>
              <p><MapPin size={14} /> {restaurant.address}</p>
              <p><Phone size={14} /> {restaurant.phone}</p>
            </RestaurantCard>
          ))}
          {!selectedRestaurant && (
            <ErrorMessage>Selecteer een restaurant</ErrorMessage>
          )}
        </FormSection>

        <FormSection>
          <SectionTitle>
            <User size={20} />
            Persoonlijke Gegevens
          </SectionTitle>
          <FormGrid>
            <FormGroup>
              <Label>
                <User size={16} />
                Naam *
              </Label>
              <Input
                {...register('name', { required: 'Naam is verplicht' })}
                className={errors.name ? 'error' : ''}
                placeholder="Jouw naam"
              />
              {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>
                <Mail size={16} />
                E-mail *
              </Label>
              <Input
                type="email"
                {...register('email', { 
                  required: 'E-mail is verplicht',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Ongeldig e-mailadres'
                  }
                })}
                className={errors.email ? 'error' : ''}
                placeholder="jouw@email.com"
              />
              {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>
                <Phone size={16} />
                Telefoonnummer
              </Label>
              <Input
                {...register('phone')}
                placeholder="+32 123 45 67 89"
              />
            </FormGroup>
          </FormGrid>
        </FormSection>

        <FormSection>
          <SectionTitle>
            <Calendar size={20} />
            Reserveringsdetails
          </SectionTitle>
          <FormGrid>
            <FormGroup>
              <Label>Datum *</Label>
              <Input
                type="date"
                {...register('date', { required: 'Datum is verplicht' })}
                className={errors.date ? 'error' : ''}
                min={getMinDate()}
              />
              {errors.date && <ErrorMessage>{errors.date.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>Tijd *</Label>
              <Select
                {...register('time', { required: 'Tijd is verplicht' })}
                className={errors.time ? 'error' : ''}
              >
                <option value="">Selecteer tijd</option>
                {timeSlots.map((time) => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </Select>
              {errors.time && <ErrorMessage>{errors.time.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>
                <Users size={16} />
                Aantal Personen *
              </Label>
              <Select
                {...register('guests', { required: 'Aantal personen is verplicht' })}
                className={errors.guests ? 'error' : ''}
              >
                <option value="">Selecteer aantal</option>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                  <option key={num} value={num}>{num} {num === 1 ? 'persoon' : 'personen'}</option>
                ))}
              </Select>
              {errors.guests && <ErrorMessage>{errors.guests.message}</ErrorMessage>}
            </FormGroup>
          </FormGrid>
        </FormSection>

        <FormSection>
          <SectionTitle>Speciale Verzoeken</SectionTitle>
          <FormGroup>
            <TextArea
              {...register('specialRequests')}
              placeholder="Speciale verzoeken, allergieën, of andere opmerkingen..."
            />
          </FormGroup>
        </FormSection>

        <SubmitButton type="submit" disabled={isSubmitting || !selectedRestaurant}>
          {isSubmitting ? 'Reservering wordt verwerkt...' : 'Reservering Bevestigen'}
        </SubmitButton>
      </ReservationForm>
    </PageContainer>
  );
};

export default ReservationPage;
